import React, { useState, useEffect, useRef } from "react";
import {
    AppBar, Toolbar, Typography, Box, Button,
    IconButton, Drawer, List, ListItem,
    ListItemButton, ListItemText, Collapse, useMediaQuery, useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocalLaundryServiceIcon from "@mui/icons-material/LocalLaundryService";
import DryCleaningIcon from "@mui/icons-material/DryCleaning";
import LayersIcon from "@mui/icons-material/Layers";
import { Link, useLocation, useNavigate } from "react-router-dom";

/* ── Data ───────────────────────────────────────────────── */
const SERVICE_ITEMS = [
  { label: "Blanchisserie de Luxe",     path: "/services/blanchisserie", icon: LocalLaundryServiceIcon, desc: "Linge, draps, housses de couette" },
  { label: "Location & Vente de Linge", path: "/services/location-linge", icon: DryCleaningIcon,        desc: "Pour hôtels et riads" },
  { label: "Nettoyage de Tapis",        path: "/services/tapis",          icon: LayersIcon,             desc: "Tapis et moquettes" },
];

const NAV_LINKS = [
  { label: "Accueil",     path: "/" },
  { label: "Services",    path: "/services", hasDropdown: true },
  { label: "Témoignages", path: "/temoignages" },
  { label: "Contact",     path: "/contact" },
];

/* ── Services Dropdown ──────────────────────────────────── */
const ServicesDropdown = ({ open }) => {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        position: "absolute",
        top: "calc(100% + 12px)",
        left: "50%",
        transform: open
          ? "translateX(-50%) translateY(0)"
          : "translateX(-50%) translateY(-10px)",
        width: 480,
        backgroundColor: "#fff",
        borderRadius: "4px",
        border: "1px solid rgba(201,168,76,0.2)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)",
        opacity: open ? 1 : 0,
        pointerEvents: open ? "all" : "none",
        transition: "opacity 0.25s ease, transform 0.25s ease",
        zIndex: 100,
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0,
          height: "3px",
          background: "linear-gradient(to right, transparent, #c9a84c, transparent)",
        },
      }}
    >
      {SERVICE_ITEMS.map(({ label, path, icon: Icon, desc }) => (
        <Box
          key={path}
          onClick={() => navigate(path)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 3,
            py: 2.2,
            cursor: "pointer",
            borderBottom: "1px solid rgba(0,0,0,0.05)",
            transition: "all 0.2s ease",
            "&:last-of-type": { borderBottom: "none" },
            "&:hover": {
              backgroundColor: "rgba(201,168,76,0.05)",
              "& .dd-arrow": { opacity: 1, transform: "translateX(0)" },
              "& .dd-icon-box": { backgroundColor: "rgba(201,168,76,0.15)", borderColor: "rgba(201,168,76,0.5)" },
              "& .dd-label": { color: "#c9a84c" },
            },
          }}
        >
          <Box
            className="dd-icon-box"
            sx={{
              width: 40, height: 40, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "1px solid rgba(201,168,76,0.2)",
              borderRadius: "6px",
              backgroundColor: "rgba(201,168,76,0.06)",
              transition: "all 0.2s ease",
            }}
          >
            <Icon sx={{ fontSize: 18, color: "#c9a84c" }} />
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              className="dd-label"
              sx={{
                color: "#1a1108",
                fontFamily: "'Playfair Display', serif",
                fontSize: "0.9rem",
                fontWeight: 600,
                mb: 0.2,
                transition: "color 0.2s ease",
              }}
            >
              {label}
            </Typography>
            <Typography sx={{ color: "rgba(0,0,0,0.45)", fontSize: "0.75rem", lineHeight: 1.4 }}>
              {desc}
            </Typography>
          </Box>

          <ArrowForwardIcon
            className="dd-arrow"
            sx={{
              fontSize: 14,
              color: "#c9a84c",
              opacity: 0,
              transform: "translateX(-6px)",
              transition: "all 0.2s ease",
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

/* ── Navbar ─────────────────────────────────────────────── */
const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const hoverRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setServicesOpen(false); }, [location]);

  const isActive = (path) =>
    path === "/services"
      ? location.pathname.startsWith("/services")
      : location.pathname === path;

  const handleServicesEnter = () => {
    clearTimeout(hoverRef.current);
    setServicesOpen(true);
  };
  const handleServicesLeave = () => {
    hoverRef.current = setTimeout(() => setServicesOpen(false), 150);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: scrolled ? "rgba(13,10,4,0.97)" : "rgba(13,10,4,0.72)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(201,168,76,0.2)",
          transition: "background-color 0.4s ease",
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 5 }, minHeight: "68px", justifyContent: "space-between" }}>

          {/* Brand */}
          <Box component={Link} to="/" sx={{ textDecoration: "none", textAlign: "center" }}>
            <Typography sx={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 700,
              fontSize: { xs: "1.15rem", md: "1.35rem" },
              color: "secondary.main",
              letterSpacing: "0.12em",
              lineHeight: 1.1,
              textTransform: "uppercase",
            }}>
              Luxury Laundry
            </Typography>
            <Typography sx={{
              fontFamily: "'Lato', sans-serif",
              fontWeight: 400,
              fontSize: "0.6rem",
              color: "rgba(201,168,76,0.65)",
              letterSpacing: "0.35em",
              mt: 0.4,
              textAlign: "center",
              textTransform: "uppercase",
            }}>
              by Zrikem Clean
            </Typography>
          </Box>

          {/* Desktop links */}
          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {NAV_LINKS.map((link) => {
                if (link.hasDropdown) {
                  return (
                    <Box
                      key={link.path}
                      onMouseEnter={handleServicesEnter}
                      onMouseLeave={handleServicesLeave}
                      sx={{ position: "relative" }}
                    >
                      <Button
                        endIcon={
                          <ExpandMoreIcon sx={{
                            fontSize: "16px !important",
                            transition: "transform 0.25s ease",
                            transform: servicesOpen ? "rotate(180deg)" : "rotate(0deg)",
                          }} />
                        }
                        sx={{
                          color: isActive(link.path) ? "secondary.main" : "rgba(255,255,255,0.82)",
                          fontSize: "0.82rem",
                          letterSpacing: "0.05em",
                          fontWeight: isActive(link.path) ? 700 : 400,
                          px: 1.8,
                          textTransform: "none",
                          cursor: "default",
                          position: "relative",
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            bottom: 2, left: "50%",
                            transform: isActive(link.path) ? "translateX(-50%) scaleX(1)" : "translateX(-50%) scaleX(0)",
                            width: "70%", height: "1px",
                            backgroundColor: "secondary.main",
                            transition: "transform 0.3s ease",
                          },
                          "&:hover": {
                            color: "secondary.main",
                            backgroundColor: "transparent",
                            "&::after": { transform: "translateX(-50%) scaleX(1)" },
                          },
                        }}
                      >
                        {link.label}
                      </Button>
                      <ServicesDropdown open={servicesOpen} />
                    </Box>
                  );
                }

                if (link.label === "Contact") {
                  return (
                    <Button
                      key={link.path}
                      component={Link} to={link.path}
                      variant="outlined"
                      sx={{
                        ml: 1.5,
                        color: "secondary.main",
                        borderColor: "secondary.main",
                        fontSize: "0.78rem",
                        letterSpacing: "0.12em",
                        fontWeight: 700,
                        px: 2.5,
                        py: 0.8,
                        borderRadius: 0,
                        textTransform: "uppercase",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: "secondary.main",
                          color: "#1a0f05",
                          borderColor: "secondary.main",
                        },
                      }}
                    >
                      Contactez-nous
                    </Button>
                  );
                }

                return (
                  <Button
                    key={link.path}
                    component={Link} to={link.path}
                    sx={{
                      color: isActive(link.path) ? "secondary.main" : "rgba(255,255,255,0.82)",
                      fontSize: "0.82rem",
                      letterSpacing: "0.05em",
                      fontWeight: isActive(link.path) ? 700 : 400,
                      px: 1.8,
                      textTransform: "none",
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 2, left: "50%",
                        transform: isActive(link.path) ? "translateX(-50%) scaleX(1)" : "translateX(-50%) scaleX(0)",
                        width: "70%", height: "1px",
                        backgroundColor: "secondary.main",
                        transition: "transform 0.3s ease",
                      },
                      "&:hover": {
                        color: "secondary.main",
                        backgroundColor: "transparent",
                        "&::after": { transform: "translateX(-50%) scaleX(1)" },
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                );
              })}
            </Box>
          )}

          {/* Mobile hamburger */}
          {isMobile && (
            <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: "#fff" }}>
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right" open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 300, backgroundColor: "#0d0a04", px: 2 } }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ px: 2 }}>
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography sx={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 700,
              fontSize: "1.2rem",
              color: "secondary.main",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              lineHeight: 1.1,
            }}>
              Luxury Laundry
            </Typography>
            <Typography sx={{
              fontFamily: "'Lato', sans-serif",
              fontWeight: 400,
              fontSize: "0.6rem",
              color: "rgba(201,168,76,0.65)",
              letterSpacing: "0.35em",
              mt: 0.5,
              textAlign: "center",
              textTransform: "uppercase",
            }}>
              by Zrikem Clean
            </Typography>
          </Box>

          <List disablePadding>
            {NAV_LINKS.map((link) =>
              link.hasDropdown ? (
                <React.Fragment key={link.path}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      sx={{ py: 1.5, borderBottom: "1px solid rgba(255,255,255,0.07)", "&:hover": { backgroundColor: "rgba(201,168,76,0.08)" } }}
                    >
                      <ListItemText
                        primary="Services"
                        primaryTypographyProps={{ sx: { color: isActive(link.path) ? "secondary.main" : "rgba(255,255,255,0.82)", fontSize: "0.9rem" } }}
                      />
                      <ExpandMoreIcon sx={{
                        color: "rgba(255,255,255,0.4)", fontSize: 18,
                        transition: "transform 0.25s ease",
                        transform: mobileServicesOpen ? "rotate(180deg)" : "none",
                      }} />
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={mobileServicesOpen}>
                    {SERVICE_ITEMS.map((s) => (
                      <ListItemButton
                        key={s.path}
                        component={Link} to={s.path}
                        onClick={() => setDrawerOpen(false)}
                        sx={{ pl: 4, py: 1.2, "&:hover": { backgroundColor: "rgba(201,168,76,0.06)" } }}
                      >
                        <ListItemText
                          primary={s.label}
                          primaryTypographyProps={{ sx: { color: "rgba(255,255,255,0.6)", fontSize: "0.82rem" } }}
                        />
                      </ListItemButton>
                    ))}
                  </Collapse>
                </React.Fragment>
              ) : (
                <ListItem key={link.path} disablePadding>
                  <ListItemButton
                    component={Link} to={link.path}
                    onClick={() => setDrawerOpen(false)}
                    sx={{ py: 1.5, borderBottom: "1px solid rgba(255,255,255,0.07)", "&:hover": { backgroundColor: "rgba(201,168,76,0.08)" } }}
                  >
                    <ListItemText
                      primary={link.label === "Contact" ? "Contactez-nous" : link.label}
                      primaryTypographyProps={{
                        sx: {
                          color: link.label === "Contact" ? "secondary.main" : isActive(link.path) ? "secondary.main" : "rgba(255,255,255,0.82)",
                          fontSize: "0.9rem",
                          fontWeight: link.label === "Contact" ? 700 : 400,
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;